/* Somewear — core: overlays, header, reveals, cursor, hero, small utilities. */
(() => {
  const S = (window.Somewear = window.Somewear || {});
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  S.reduceMotion = () => reduceMotion.matches;

  S.debounce = (fn, wait = 200) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  S.parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  S.announce = (message) => {
    const live = document.querySelector('[data-live]');
    if (!live) return;
    live.textContent = '';
    window.setTimeout(() => (live.textContent = message), 50);
  };

  let toastTimer;
  S.toast = (message) => {
    const toast = document.querySelector('[data-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => (toast.hidden = true), 3200);
  };

  /* ---------------------------------------------------------------------
     Overlays (cart drawer, menu drawer, search): open/close, focus trap,
     scroll lock, Escape to close, focus return.
     --------------------------------------------------------------------- */
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  const openOverlays = new Set();

  const lockScroll = () => {
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.paddingRight = scrollbar > 0 ? `${scrollbar}px` : '';
    document.body.classList.add('is-locked');
  };
  const unlockScroll = () => {
    if (openOverlays.size) return;
    document.body.classList.remove('is-locked');
    document.body.style.paddingRight = '';
  };

  class Overlay extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[role="dialog"]');
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) {
          event.preventDefault();
          this.close();
        }
      });
      this.addEventListener('keydown', (event) => this.onKeydown(event));
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(trigger) {
      if (this.isOpen) return;
      openOverlays.forEach((overlay) => overlay !== this && overlay.close({ restoreFocus: false }));
      this.trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      openOverlays.add(this);
      lockScroll();
      if (this.trigger && this.trigger.setAttribute) this.trigger.setAttribute('aria-expanded', 'true');
      window.requestAnimationFrame(() => {
        const target = this.initialFocus() || this.panel;
        if (target) target.focus({ preventScroll: true });
      });
      this.onOpen();
    }

    close({ restoreFocus = true } = {}) {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      openOverlays.delete(this);
      unlockScroll();
      if (this.trigger && this.trigger.setAttribute) this.trigger.setAttribute('aria-expanded', 'false');
      if (restoreFocus && this.trigger && document.contains(this.trigger)) this.trigger.focus({ preventScroll: true });
      this.onClose();
    }

    initialFocus() {
      return null;
    }
    onOpen() {}
    onClose() {}

    onKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusables = [...this.panel.querySelectorAll(FOCUSABLE)].filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }
  S.Overlay = Overlay;

  class MenuDrawer extends Overlay {}
  customElements.define('menu-drawer', MenuDrawer);

  // Any element with data-open="<overlay id>" opens that overlay.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-open]');
    if (!trigger) return;
    const overlay = document.getElementById(trigger.dataset.open);
    if (!overlay || typeof overlay.open !== 'function') return;
    event.preventDefault();
    overlay.open(trigger);
  });

  // "/" opens search, as long as the visitor isn't typing somewhere.
  document.addEventListener('keydown', (event) => {
    if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
    const tag = document.activeElement && document.activeElement.tagName;
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) || document.activeElement.isContentEditable) return;
    const search = document.getElementById('SearchOverlay');
    if (search && search.open) {
      event.preventDefault();
      search.open(document.activeElement);
    }
  });

  /* ---------------------------------------------------------------------
     Native <dialog> helpers (size guide)
     --------------------------------------------------------------------- */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open-dialog]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.openDialog);
      if (dialog && dialog.showModal) {
        dialog.showModal();
        document.body.classList.add('is-locked');
        dialog.addEventListener('close', () => unlockScroll(), { once: true });
      }
      return;
    }
    if (event.target.closest('[data-close-dialog]')) {
      event.target.closest('dialog').close();
      return;
    }
    // Click on the dialog backdrop closes it.
    if (event.target.matches('dialog.modal')) event.target.close();
  });

  /* ---------------------------------------------------------------------
     Header: compact on scroll, desktop dropdowns
     --------------------------------------------------------------------- */
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.ticking = false;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();

      this.dropdowns = [...this.querySelectorAll('[data-dropdown]')];
      this.dropdowns.forEach((details) => {
        details.addEventListener('toggle', () => {
          if (details.open) this.dropdowns.forEach((other) => other !== details && (other.open = false));
          this.classList.toggle('has-dropdown', this.dropdowns.some((d) => d.open));
        });
        if (finePointer.matches) {
          let closeTimer;
          details.addEventListener('mouseenter', () => {
            clearTimeout(closeTimer);
            details.open = true;
          });
          details.addEventListener('mouseleave', () => {
            closeTimer = setTimeout(() => (details.open = false), 120);
          });
        }
        details.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && details.open) {
            details.open = false;
            details.querySelector('summary').focus();
          }
        });
      });

      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.dropdowns.forEach((d) => (d.open = false));
      });
      this.addEventListener('focusout', (event) => {
        this.dropdowns.forEach((d) => {
          if (d.open && event.relatedTarget && !d.contains(event.relatedTarget)) d.open = false;
        });
      });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.classList.toggle('is-scrolled', window.scrollY > 24);
        this.ticking = false;
      });
    }
  }
  customElements.define('site-header', SiteHeader);

  /* ---------------------------------------------------------------------
     Scroll reveals
     --------------------------------------------------------------------- */
  const REVEAL_SELECTOR = '[data-reveal], [data-reveal-lines]';
  let revealObserver = null;

  if (S.settings && S.settings.reveals && !reduceMotion.matches && 'IntersectionObserver' in window) {
    revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
  }

  S.observeReveals = (root = document) => {
    root.querySelectorAll(REVEAL_SELECTOR).forEach((el) => {
      if (el.classList.contains('is-visible')) return;
      if (revealObserver) revealObserver.observe(el);
      else el.classList.add('is-visible');
    });
  };

  /* ---------------------------------------------------------------------
     Cursor label (desktop, fine pointer, motion allowed)
     --------------------------------------------------------------------- */
  const initCursor = () => {
    if (!S.settings || !S.settings.cursor || !finePointer.matches || reduceMotion.matches) return;
    const cursor = document.querySelector('[data-cursor-el]');
    const label = cursor && cursor.querySelector('[data-cursor-label]');
    if (!cursor || !label) return;

    let x = 0;
    let y = 0;
    let frame = null;
    const move = () => {
      cursor.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      frame = null;
    };

    document.addEventListener(
      'pointermove',
      (event) => {
        if (event.pointerType !== 'mouse') return;
        x = event.clientX;
        y = event.clientY;
        if (!frame) frame = window.requestAnimationFrame(move);
      },
      { passive: true }
    );

    document.addEventListener('pointerover', (event) => {
      const target = event.target.closest('[data-cursor]');
      if (target) {
        label.textContent = target.dataset.cursor;
        cursor.classList.add('is-active');
      } else {
        cursor.classList.remove('is-active');
      }
    });
    document.addEventListener('pointerleave', () => cursor.classList.remove('is-active'));
  };

  /* ---------------------------------------------------------------------
     Hero: pointer parallax + background video control
     --------------------------------------------------------------------- */
  class HeroBanner extends HTMLElement {
    connectedCallback() {
      this.media = this.querySelector('[data-hero-media]');
      this.video = this.querySelector('video');
      this.pauseButton = this.querySelector('[data-hero-pause]');

      if (this.video) {
        this.video.muted = true;
        if (reduceMotion.matches) {
          this.video.removeAttribute('autoplay');
          this.video.pause();
          this.setPaused(true);
        } else {
          const attempt = this.video.play();
          if (attempt && attempt.catch) attempt.catch(() => this.setPaused(true));
        }
        if (this.pauseButton) {
          this.pauseButton.addEventListener('click', () => {
            if (this.video.paused) {
              this.video.play();
              this.setPaused(false);
            } else {
              this.video.pause();
              this.setPaused(true);
            }
          });
        }
      }

      if (finePointer.matches && !reduceMotion.matches && this.media) this.initParallax();
    }

    setPaused(paused) {
      if (!this.pauseButton) return;
      this.pauseButton.setAttribute('aria-pressed', String(paused));
      this.pauseButton.querySelector('[data-label-playing]').hidden = paused;
      this.pauseButton.querySelector('[data-label-paused]').hidden = !paused;
    }

    initParallax() {
      let targetX = 0;
      let targetY = 0;
      let currentX = 0;
      let currentY = 0;
      let running = false;

      const step = () => {
        currentX += (targetX - currentX) * 0.08;
        currentY += (targetY - currentY) * 0.08;
        this.media.style.setProperty('--mx', currentX.toFixed(4));
        this.media.style.setProperty('--my', currentY.toFixed(4));
        if (Math.abs(targetX - currentX) > 0.001 || Math.abs(targetY - currentY) > 0.001) {
          window.requestAnimationFrame(step);
        } else {
          running = false;
        }
      };
      const kick = () => {
        if (running) return;
        running = true;
        window.requestAnimationFrame(step);
      };

      this.addEventListener('pointermove', (event) => {
        const rect = this.getBoundingClientRect();
        targetX = (event.clientX - rect.left) / rect.width - 0.5;
        targetY = (event.clientY - rect.top) / rect.height - 0.5;
        kick();
      });
      this.addEventListener('pointerleave', () => {
        targetX = 0;
        targetY = 0;
        kick();
      });
    }
  }
  customElements.define('hero-banner', HeroBanner);

  /* ---------------------------------------------------------------------
     Fit text: scales a one-line wordmark to exactly fill its container.
     CSS sets a close fallback size; this corrects it for the actual font.
     --------------------------------------------------------------------- */
  const fitText = (el) => {
    const inner = el.querySelector('[data-fit-inner]') || el;
    el.style.fontSize = '';
    const base = parseFloat(window.getComputedStyle(el).fontSize);
    const available = el.clientWidth;
    const needed = inner.scrollWidth;
    if (!available || !needed) return;
    el.style.fontSize = `${Math.floor(base * (available / needed) * 0.995 * 100) / 100}px`;
  };

  const initFitText = (root = document) => {
    const targets = root.querySelectorAll('[data-fit-text]');
    if (!targets.length) return;
    targets.forEach(fitText);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => targets.forEach(fitText));
    if ('ResizeObserver' in window) {
      let lastWidths = new WeakMap();
      const observer = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const width = Math.round(entry.contentRect.width);
          if (lastWidths.get(entry.target) === width) return;
          lastWidths.set(entry.target, width);
          fitText(entry.target);
        });
      });
      targets.forEach((el) => observer.observe(el));
    }
  };

  /* ---------------------------------------------------------------------
     Misc
     --------------------------------------------------------------------- */
  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-autosubmit]')) event.target.form.submit();
  });

  const init = () => {
    S.observeReveals();
    initCursor();
    initFitText();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Theme editor: re-run reveals for sections added or re-rendered in the editor.
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-reveal], [data-reveal-lines]').forEach((el) => el.classList.add('is-visible'));
    initFitText(event.target);
  });
})();
